import { FunctionComponent, useEffect, useState } from "react";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import {
	SignalPreview,
	getLinkedinActorName,
	getLinkedinPostMedia,
	getLinkedinPostText,
	getLinkedinPostUrl,
	getLinkedinResharedPost,
} from "./signalTypes";
import { LinkedinResharedPost } from "./LinkedinResharedPost";
import { LinkedinSignalPostMedia } from "./LinkedinSignalPostMedia";

interface LinkedinSignalPostPreviewProps {
	signal: SignalPreview;
}

const formatPostDate = (date?: string | Date | null): string => {
	if (!date) {
		return "";
	}

	const dateObj = typeof date === "string" ? new Date(date) : date;

	return Number.isNaN(dateObj.getTime()) ? "" : format(dateObj, "d MMM yyyy");
};

export const LinkedinSignalPostPreview: FunctionComponent<LinkedinSignalPostPreviewProps> = ({
	signal,
}) => {
	const { t } = useTranslation();
	const [hasImageError, setHasImageError] = useState(false);
	const post = signal.linkedinPost;
	const actor = post?.actor;
	const postUrl = getLinkedinPostUrl(signal);
	const actorName = getLinkedinActorName(signal, t("profileTracker.signalTimeline.linkedinPost"));
	const actorUrl = actor?.actor_navigationContext;
	const postText = getLinkedinPostText(signal);
	const media = getLinkedinPostMedia(signal);
	const resharedPost = getLinkedinResharedPost(signal);
	const postDate = formatPostDate(post?.postedAt || signal.publishedDate);
	const likes = post?.social_details?.numLikes ?? 0;
	const comments = post?.social_details?.numComments ?? 0;
	const shares = post?.social_details?.numShares ?? 0;

	useEffect(() => {
		setHasImageError(false);
	}, [actor?.actor_image]);

	const fallbackAvatar = (
		<div className="flex h-10 w-10 items-center justify-center rounded-full bg-background-light text-sm font-semibold text-text-meta">
			{actorName.charAt(0).toUpperCase()}
		</div>
	);

	const avatar =
		actor?.actor_image && !hasImageError ? (
			<img
				alt={actorName}
				className="h-10 w-10 rounded-full object-cover"
				src={actor.actor_image}
				onError={() => setHasImageError(true)}
			/>
		) : (
			fallbackAvatar
		);

	return (
		<div className="mb-3 rounded-lg border border-border-light bg-white p-3">
			<div className="mb-3 flex items-start gap-3">
				{actorUrl ? (
					<Link target="_blank" to={actorUrl} onClick={(event) => event.stopPropagation()}>
						{avatar}
					</Link>
				) : (
					avatar
				)}
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1">
						{actorUrl ? (
							<Link
								className="truncate text-sm font-semibold text-text hover:underline"
								target="_blank"
								to={actorUrl}
								onClick={(event) => event.stopPropagation()}
							>
								{actorName}
							</Link>
						) : (
							<span className="truncate text-sm font-semibold text-text">{actorName}</span>
						)}
						<LinkedinIcon />
					</div>
					{actor?.actor_description && (
						<div className="truncate text-xs text-text-meta">{actor.actor_description}</div>
					)}
					{postDate && <div className="text-xs text-text-meta">{postDate}</div>}
				</div>
			</div>

			{postText && <p className="whitespace-pre-wrap text-sm leading-5 text-text">{postText}</p>}

			{media.length > 0 && <LinkedinSignalPostMedia media={media} postUrl={postUrl} />}

			{resharedPost && <LinkedinResharedPost postUrl={postUrl} reshared={resharedPost} />}

			<div className="mt-3 flex items-center justify-between border-t border-border-light pt-3 text-xs text-text-meta">
				<span>{t("profileTracker.signalTimeline.linkedinLikes", { count: likes })}</span>
				<span>{t("profileTracker.signalTimeline.linkedinComments", { count: comments })}</span>
				<span>{t("profileTracker.signalTimeline.linkedinShares", { count: shares })}</span>
				{postUrl && (
					<Link
						className="font-semibold text-blue-action hover:underline"
						target="_blank"
						to={postUrl}
						onClick={(event) => event.stopPropagation()}
					>
						{t("profileTracker.signalTimeline.viewOnLinkedin")}
					</Link>
				)}
			</div>
		</div>
	);
};
